/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useCallback, useMemo, memo } from "react";
import { css } from "@panda/css";
import { CosmosButton, CosmosButtonProps } from "@components/elements";
import { AnalyticEventProps } from "@components/types";
import { useTabsContext } from "./tabs.context";

export interface CosmosTabsButtonProps {
	/** The numeric index that this tab represents */
	index: number;
	/** Tab label text */
	label: string;
	/** Optional count to display (e.g., "Upcoming (5)") */
	count?: number;
	/** Whether this tab is disabled */
	disabled?: boolean;
	/** Custom CSS class name */
	className?: string;
	/** Analytics event props */
	analytics?: AnalyticEventProps;
}

/**
 * Individual tab button. Must have a unique numeric index that corresponds to a Tabs.Panel.
 */
export const CosmosTabsButton = memo(
	({ index, label, count, disabled = false, className, analytics }: CosmosTabsButtonProps): React.JSX.Element => {
		const context = useTabsContext();
		const { activeIndex, onIndexChange, size, activeVariant, inactiveVariant, hasActivePanel, idPrefix } = context;

		const isSelected = activeIndex === index;

		// Build the label with optional count
		const displayLabel = count !== undefined ? `${label} (${count})` : label;

		const handleClick = useCallback((): void => {
			if (!disabled && onIndexChange) {
				onIndexChange(index);
			}
		}, [disabled, onIndexChange, index]);

		const finalAnalytics = useMemo<AnalyticEventProps>(
			() => ({
				eventCategory: analytics?.eventCategory || "button",
				interactionType: analytics?.interactionType || "click",
				eventAction: analytics?.eventAction || "select",
				eventLabel: analytics?.eventLabel || label,
				eventValue: {
					...analytics?.eventValue,
					component: analytics?.eventValue?.component || "CosmosTabsTrigger",
					index: analytics?.eventValue?.index || index,
				},
			}),
			[analytics, label, index],
		);

		const buttonVariant = isSelected ? activeVariant : inactiveVariant;

		const buttonStyles = css({
			borderRadius: "eds.borderRadius.full",
			textStyle: "uiMD",
			"&.secondary": {
				border: "1px solid",
				borderColor: "eds.border.subtle",
				background: "eds.palette.transparent.light.005",
				_hover: disabled
					? {}
					: {
							background: "eds.palette.transparent.light.005",
							borderColor: "eds.border.default",
						},
			},
			"&.contrast": {
				_hover: disabled
					? {}
					: {
							borderColor: "eds.fill.contrast.hover",
						},
			},
			...(disabled && {
				pointerEvents: "none",
				opacity: 0.5,
			}),
			smDown: {
				borderRadius: "eds.borderRadius.full",
			},
		});

		const tabId = `${idPrefix}tab-${index}`;
		const panelId = `${idPrefix}panel-${index}`;

		// The tab role sits on the button itself: a role="tab" wrapper around a
		// button is a nested interactive control. Only the selected panel renders,
		// so aria-controls is set only when that panel exists.
		return (
			<div role="presentation">
				<CosmosButton
					role="tab"
					id={tabId}
					aria-selected={isSelected}
					aria-controls={isSelected && hasActivePanel ? panelId : undefined}
					label={displayLabel}
					variant={buttonVariant as CosmosButtonProps["variant"]}
					size={size}
					disabled={disabled}
					onClick={handleClick}
					className={`${buttonStyles} ${buttonVariant} ${isSelected ? "tab-button-active" : ""} ${className || ""}`}
					analytics={finalAnalytics}
				/>
			</div>
		);
	},
);

CosmosTabsButton.displayName = "CosmosTabs.Button";
